// Generalized key/value preferences backed by the embedded Bun main
// process's disk store at /api/preferences. Provides a synchronous
// localStorage-like API on top of an async backend so existing
// settings modules (sound, graphics, controls, etc.) can be migrated
// off raw localStorage without rewriting every read site as async.
//
// Why this exists: localStorage is partitioned per-origin (host+port).
// The embedded renderer server binds to a stable preferred port but
// can still land on a random fallback port if conflicts arise, which
// wipes the origin's localStorage and with it every user-facing
// setting. A proper desktop app persists settings to a file in the OS
// app-data dir, which is exactly what /api/preferences writes to
// (~/Library/Application Support/com.valkenhall.app/data/preferences.json
// on macOS, and the analogous dirs on Windows/Linux).
//
// Two-tier storage:
//   1. In-memory cache (`Map`). Populated once at boot from the
//      backend; every read hits this O(1).
//   2. localStorage. Used as a best-effort mirror so pre-hydration
//      reads still return something sensible, and so the first launch
//      after this migration doesn't force the user to reconfigure.
//
// Writes: update cache + localStorage synchronously, fire-and-forget a
// PUT to /api/preferences. Persisted state catches up in the
// background and survives anything (port changes, app updates, etc.).

import { LOCAL_API_ORIGIN } from './localApi';

const cache = new Map();
let hydrated = false;
let hydratePromise = null;

// Known legacy localStorage keys to migrate into the backend the first
// time this file runs on a given install. Values migrate transparently
// After the first hydrate the backend is authoritative.
const MIGRATION_KEYS = [
  'fab-arena-sound-settings',
  'fab-graphics-settings',
  'vk-control-settings',
  'fab-arena-discord-settings',
  'valkenhall-ambience-settings',
  'valkenhall-playmat',
];

function safeGetLocal(key) {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
  catch { return null; }
}

function safeSetLocal(key, value) {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(key, value); }
  catch { /* private mode / quota. Ignore */ }
}

function tryParseJSON(raw) {
  if (raw == null) return raw;
  try { return JSON.parse(raw); } catch { return raw; }
}

function persistWrite(key, value) {
  try {
    fetch(`${LOCAL_API_ORIGIN}/api/preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, value }),
    }).catch(() => {});
  } catch { /* ignore */ }
}

/**
 * Fetch every persisted preference into memory and mirror them into
 * localStorage so same-origin sync reads stay consistent with the
 * backend. Idempotent. Safe to call multiple times.
 *
 * Call (and await) this at app boot before rendering if you want
 * first-frame correctness; otherwise callers that happen to read
 * before hydration finishes fall back to localStorage, which is
 * usually also correct.
 */
export function hydratePrefs() {
  if (hydrated) return Promise.resolve();
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    try {
      const res = await fetch(`${LOCAL_API_ORIGIN}/api/preferences`);
      if (res.ok) {
        const data = await res.json();
        if (data && typeof data === 'object') {
          for (const [k, v] of Object.entries(data)) {
            cache.set(k, v);
            if (typeof v === 'string') safeSetLocal(k, v);
            else if (v !== null && v !== undefined) safeSetLocal(k, JSON.stringify(v));
          }
        }
      }
    } catch {
      // Backend unavailable (tests, pure-browser dev). LocalStorage
      // fallback covers us.
    }
    // Migrate any legacy localStorage values the backend doesn't know
    // about yet. One-time cost per install; silent no-op after.
    for (const key of MIGRATION_KEYS) {
      if (cache.has(key)) continue;
      const raw = safeGetLocal(key);
      if (raw == null) continue;
      const parsed = tryParseJSON(raw);
      cache.set(key, parsed);
      persistWrite(key, parsed);
    }
    hydrated = true;
  })();
  return hydratePromise;
}

/**
 * Read a preference synchronously. Returns `defaultValue` if the key
 * isn't set anywhere. Values stored as JSON are returned as parsed
 * objects; values stored as strings are returned as strings.
 */
export function getPref(key, defaultValue = null) {
  if (cache.has(key)) return cache.get(key);
  const raw = safeGetLocal(key);
  if (raw == null) return defaultValue;
  const parsed = tryParseJSON(raw);
  cache.set(key, parsed);
  return parsed;
}

/**
 * Write a preference synchronously. Mirrors to localStorage and fires
 * a background PUT to persist to the OS app-data dir. The call
 * returns immediately; persistence happens on the next tick.
 */
export function setPref(key, value) {
  cache.set(key, value);
  try {
    if (typeof value === 'string') safeSetLocal(key, value);
    else safeSetLocal(key, JSON.stringify(value));
  } catch { /* ignore */ }
  persistWrite(key, value);
}

/**
 * Remove a preference. Mirrors the removal to localStorage and the
 * backend.
 */
export function removePref(key) {
  cache.delete(key);
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem(key); }
  catch { /* ignore */ }
  // Backend treats null as "forget this key".
  persistWrite(key, null);
}

// Kick off hydration as soon as the module loads. Consumers that want
// first-frame correctness should await `hydratePrefs()` explicitly.
// but even without that, the cache will be populated within a tick
// and every subsequent read gets the real value.
if (typeof window !== 'undefined') {
  hydratePrefs();
}
